import React from 'react';
import { Helmet } from 'react-helmet-async';
import { getSiteUrl } from '../../utils/siteConfig';

const SITE_NAME = 'Cool & Care';
const DEFAULT_OG_IMAGE_ALT = 'Cool & Care - Integrated Security, IT, HVAC, Fire Safety and Solar Solutions in DRC';
const DEFAULT_ROBOTS = 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  keywords?: string;
  noindex?: boolean;
  article?: {
    publishedTime?: string;
    author?: string;
  };
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonical,
  ogImage,
  ogType = 'website',
  keywords,
  noindex = false,
  article,
}) => {
  const siteUrl = getSiteUrl();
  const defaultOgImage = `${siteUrl}/assets/images/OGimage/og-coolandcare.png`;
  const resolvedOgImage = ogImage || defaultOgImage;
  const fullTitle = `${title} | ${SITE_NAME}`;
  const canonicalUrl = canonical || `${siteUrl}${window.location.pathname}`;

  return (
    <Helmet>
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content={noindex ? 'noindex,nofollow' : DEFAULT_ROBOTS} />
      <meta name="googlebot" content={noindex ? 'noindex,nofollow' : DEFAULT_ROBOTS} />
	  <meta name="google-site-verification" content="BWweHRXdw0ACiR3zggwLdSi9A4RpEQmuzK1H5V0XFOA" />

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={resolvedOgImage} />
      <meta property="og:image:secure_url" content={resolvedOgImage} />
      <meta property="og:image:alt" content={DEFAULT_OG_IMAGE_ALT} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={resolvedOgImage} />
      <meta name="twitter:image:alt" content={DEFAULT_OG_IMAGE_ALT} />

      {article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {article?.author && (
        <meta property="article:author" content={article.author} />
      )}

      <link rel="alternate" hrefLang="en" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
    </Helmet>
  );
};

export { SITE_NAME };
export default SEO;
